import React, { FC } from 'react'
import { Link, Paper, Stack } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { SensorOrder } from 'src/api'
import { TableToolbar } from 'src/components/ui'

interface SensorUploadOrdersProps {
  orders: SensorOrder[]
  onDownloadArchive: (archiveId: number) => void
}

export const SensorUploadOrders: FC<SensorUploadOrdersProps> = ({ orders, onDownloadArchive }) => {
  const columns: GridColDef<SensorOrder>[] = [
    {
      field: 'orderDate',
      headerName: 'Order Date',
      flex: 1,
      minWidth: 110,
      sortable: false,
      disableColumnMenu: true,
      // orderDate is a date-only 'yyyy-MM-dd' string — parseISO keeps it in the local timezone
      valueFormatter: ({ value }) => (value ? format(parseISO(value as string), 'P') : '-')
    },
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: 'sensorsCount',
      headerName: 'Sensors Amount',
      flex: 1,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: 'archives',
      headerName: 'QR-codes',
      flex: 2,
      minWidth: 280,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: ({ row }) => (row.archives || []).map((archive) => archive.originalFileName || archive.fileName).join(', '),
      renderCell: ({ row }) =>
        row.archives?.length ? (
          <Stack sx={{ py: 1 }}>
            {row.archives.map((archive) => (
              <Link key={archive.id} component="button" type="button" variant="body2" onClick={() => onDownloadArchive(archive.id)} sx={{ textAlign: 'left' }}>
                {archive.originalFileName || archive.fileName}
              </Link>
            ))}
          </Stack>
        ) : (
          '-'
        )
    }
  ]

  return (
    <Paper>
      <DataGridPremium<SensorOrder>
        pagination={false}
        autoHeight
        disableRowSelectionOnClick
        rows={orders}
        columns={columns}
        rowCount={orders.length}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        rowThreshold={2}
        columnThreshold={2}
        hideFooterPagination
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
        slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_orders`} /> }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell': { minHeight: 52 },
          '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            lineHeight: 1,
            fontSize: '13px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            overflow: 'visible'
          }
        }}
      />
    </Paper>
  )
}
